import type { RouteLocationNamedRaw } from 'vue-router'
import type { Cargo, PatchedTransport, Transport, TransportByHash } from '@apiTypes'
import { TransportationModeEnum, TransportStatusEnum } from '@apiTypes'

import type { FeatureAvailability } from '@/types/feature-availability'
import type { TransportFormType } from '@/views/transports/types/transport-form'
import i18n from '@/plugins/i18n'
import { isNullOrUndefined } from '@/utils/common.utils'

export function transportDetailTo(id: number): RouteLocationNamedRaw {
  return { name: 'transport-view-id', params: { id } }
}

export function createTransportStateFilterItems(): Ref<{ value: TransportStatusEnum | ''; label: string }[]> {
  const { t } = i18n.global

  return computed(() => [
    { value: '', label: t('All') },
    { value: TransportStatusEnum.CANCELED, label: t('Canceled') },
    { value: TransportStatusEnum.FINISHED, label: t('Finished') },
    { value: TransportStatusEnum.PROCESSING, label: t('Processing') },
    { value: TransportStatusEnum.CANCELED_BY_CARRIER, label: t('Canceled by carrier') },
    { value: TransportStatusEnum.CANCELED_BY_SUPPORT, label: t('Canceled by support') },
  ])
}

export function transportMessagingAvailability(hasPermissions: boolean, transport: Transport): FeatureAvailability {
  const { t } = i18n.global

  if (!hasPermissions)
    return { enabled: false, reason: t('You don’t have permissions to send messages to carriers.') }

  if (transport.status && isTransportStatusCanceled(transport.status))
    return { enabled: false, reason: t('Messages are disabled, because the transport was canceled.') }
  if (transport.carrier.isDeleted)
    return { enabled: false, reason: t('Messages are disabled, because the carrier was deleted from address book.') }

  return { enabled: true }
}

export function transportUpdatesAvailability(hasPermissions: boolean, transport: Transport | TransportByHash | undefined | null): FeatureAvailability {
  const { t } = i18n.global

  if (!transport)
    return { enabled: false }

  if (!hasPermissions)
    return { enabled: false, reason: t('You don’t have permissions to create updates.') }

  if (transport.status === TransportStatusEnum.FINISHED)
    return { enabled: false, reason: t('Updates are disabled, because the transport was finished.') }
  if (transport.status && isTransportStatusCanceled(transport.status))
    return { enabled: false, reason: t('Updates are disabled, because the transport was canceled.') }

  return { enabled: true }
}

export function isIntermodalTransport(transportationMode: TransportationModeEnum | undefined) {
  if (!transportationMode) return false

  return transportationMode === TransportationModeEnum.INTERMODAL
}

const CANCELED_TRANSPORT_STATUSES = new Set<TransportStatusEnum>([
  TransportStatusEnum.CANCELED,
  TransportStatusEnum.CANCELED_BY_CARRIER,
  TransportStatusEnum.CANCELED_BY_SUPPORT,
])

export function isTransportStatusCanceled(status: TransportStatusEnum | null | undefined): boolean {
  return !isNullOrUndefined(status) && CANCELED_TRANSPORT_STATUSES.has(status)
}

/** A transport the user can still book against. A missing status is not processing. */
export function isTransportStatusProcessing(status: TransportStatusEnum | null | undefined): boolean {
  return status === TransportStatusEnum.PROCESSING
}

export function isTransportStatusFinished(status: TransportStatusEnum | null | undefined): boolean {
  return status === TransportStatusEnum.FINISHED
}

export function formValuesToTransportUpdatePayload(values: TransportFormType, cargo: Cargo): PatchedTransport {
  return {
    waypoints: values.waypoints,
    routeType: values.routeType,
    transportationMode: values.transportationMode,
    note: values.note,
    reference: values.reference,
    costCenter: values.costCenter,
    incoterms: values.incoterms,
    vehicleTypeId: values.vehicleTypeId,
    responsibleUserId: values.responsibleUserId,
    tags: values.tags,
    followers: values.followers,
    cargo,
    resolvingRequests: values.mergedFrom,
  }
}
